/**
 * CSV 解析與產生(RFC 4180)(FIN-09)
 *
 * 支援含逗號、雙引號、換行的引號欄位,修正 line.split(',') 對引號欄位的錯位問題。
 */

/**
 * 解析單行 CSV 為欄位陣列
 * - 引號欄位內的逗號不分隔
 * - 引號內的 "" 表示字面雙引號
 * - 引號欄位內可含換行(此場景較少見,仍支援)
 */
export function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++; // 跳過跳脫的雙引號
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        fields.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }

  fields.push(current);
  return fields;
}

/** 將欄位陣列編碼為單行 CSV(含必要的引號跳脫,與匯出端一致) */
export function toCsvLine(fields: (string | number | null | undefined)[]): string {
  return fields
    .map((field) => {
      const value = field === null || field === undefined ? '' : String(field);
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    })
    .join(',');
}

/**
 * 將整段 CSV 文字解析為列陣列(支援引號內含換行的多行欄位)
 *
 * 以「引號平衡」串接續行:一列的引號數為偶數才算完整,
 * 奇數表示引號內含換行,需與下一實體行合併後再 parseCsvLine。
 */
export function parseCsvText(text: string): string[][] {
  const physicalLines = text.split(/\r?\n/);
  const rows: string[][] = [];
  let buffer = '';
  let inQuotes = false;

  for (const line of physicalLines) {
    buffer = buffer === '' ? line : `${buffer}\n${line}`;

    // 計算不在跳脫(„"")狀態下的引號個數以判定平衡
    let quotes = 0;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        // "" 為跳脫,成對消掉
        if (line[i + 1] === '"') {
          i++;
          continue;
        }
        quotes++;
      }
    }
    inQuotes = quotes % 2 === 1 ? !inQuotes : inQuotes;

    if (!inQuotes) {
      const row = parseCsvLine(buffer);
      // 全空行略過
      if (row.some((field) => field.trim() !== '')) {
        rows.push(row);
      }
      buffer = '';
    }
  }

  // 未收尾的不平衡內容(檔尾斷在引號內)仍嘗試解析
  if (buffer !== '') {
    rows.push(parseCsvLine(buffer));
  }

  return rows;
}

/**
 * 取得本地時區(today)的 yyyy-MM-dd 日期字串(UI-06)
 * new Date().toISOString() 是 UTC,台灣凌晨 0-8 點會得到「昨天」
 */
export function toLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
